import { Popover } from "antd";
import { Workspace } from "./workspace";

import DuplicateIcon from "@/assets/icons/duplicate.svg?react";
import EditIcon from "@/assets/icons/rename.svg?react";
import DeleteIcon from "@/assets/icons/delete.svg?react";
import { EllipsisOutlined } from "@ant-design/icons";
import { WorkspaceModal } from "./SaveWorkspaceButton/WorkspaceModal";
import { DeleteWorkspaceModal } from "./DeleteWorkspaceModal";
import { useState } from "react";
import { useDontAskAgain } from "@/utils/useDontAskAgain";
import { useSuperuserMode } from "@/contexts/useSuperuser";

type WorkspaceItemMenuProps = {
  workspace: Workspace;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onDuplicate: (workspaceId: number) => void;
  onRename: (
    workspaceId: number,
    newTitle: string,
    isVivoTemplate?: boolean,
  ) => void;
  onDelete: (workspaceId: number) => void;
  existingNames?: string[];
};

const MenuItem = ({
  icon,
  label,
  onClick,
  disabled,
  testId,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  testId: string;
}) => {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`flex items-center gap-1 px-4 py-2 hover:bg-gray-100 text-[#2B4752] text-[14px] ${disabled ? "opacity-50 cursor-default" : ""}`}
      disabled={disabled}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
};

export function WorkspaceItemMenu({
  workspace,
  isOpen,
  onOpenChange,
  onDuplicate,
  onRename,
  onDelete,
  existingNames,
}: WorkspaceItemMenuProps) {
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { dontAskAgain, setDontAskAgain } = useDontAskAgain(
    "deleteWorkspaceModalDontAskAgain",
  );
  const { isSuperuserMode } = useSuperuserMode();

  const canModifyVivoTemplate = isSuperuserMode;
  const isDisabled = workspace.is_vivo_template && !canModifyVivoTemplate;

  const menuContent = (
    <div data-testid="workspace-item-menu" className="flex flex-col ">
      <MenuItem
        icon={<DuplicateIcon className="w-5 h-5 " />}
        label="Duplicate"
        testId="workspace-item-menu-duplicate"
        onClick={() => {
          onDuplicate(workspace.id);
          onOpenChange(false);
        }}
      />
      <MenuItem
        disabled={isDisabled}
        icon={<EditIcon className="w-5 h-5 " />}
        label="Rename"
        testId="workspace-item-menu-rename"
        onClick={() => {
          setRenameModalOpen(true);
          onOpenChange(false);
        }}
      />
      <MenuItem
        disabled={isDisabled}
        icon={<DeleteIcon className="w-5 h-5 " />}
        label="Delete"
        testId="workspace-item-menu-delete"
        onClick={() => {
          if (dontAskAgain) {
            onDelete(workspace.id);
          } else {
            setDeleteModalOpen(true);
          }
          onOpenChange(false);
        }}
      />
    </div>
  );

  return (
    <>
      <WorkspaceModal
        isOpen={renameModalOpen}
        onCancel={() => setRenameModalOpen(false)}
        onConfirm={(name: string, isVivoTemplate?: boolean) => {
          onRename(workspace.id, name, isVivoTemplate);
          setRenameModalOpen(false);
        }}
        workspace={workspace}
        existingNames={existingNames}
      />
      <DeleteWorkspaceModal
        id={workspace.id.toString()}
        saveDontAskAgain={setDontAskAgain}
        isOpen={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        onConfirm={() => {
          onDelete(workspace.id);
        }}
      />
      <Popover
        content={menuContent}
        trigger="click"
        placement="bottomRight"
        arrow={false}
        open={isOpen}
        onOpenChange={onOpenChange}
        styles={{
          content: {
            paddingTop: 2,
            paddingBottom: 2,
            paddingLeft: 2,
            paddingRight: 2,
          },
        }}
        destroyOnHidden={true}
      >
        <button
          data-testid="workspace-item-menu-trigger"
          onClick={(e) => e.stopPropagation()}
          className="  rounded-sm absolute top-[2px] right-3"
        >
          <EllipsisOutlined size={24} className="text-[#5F6368]" />
        </button>
      </Popover>
    </>
  );
}
